<script setup lang="ts">
import {
  computed,
  inject,
  ref,
  watch,
  onMounted,
  nextTick,
  onBeforeUnmount,
} from 'vue';
import { useStore } from '@/stores/main';
import BButton from '@/components/BButton.vue';
import { useRouter } from 'vue-router';

const props = defineProps({
  currentSectionIdx: { type: Number, required: true },
  sectionsCount: { type: Number, required: true },
  isLoading: { type: Boolean, default: false },
  questionIndex: { type: Number, default: 0 },
  questionCount: { type: Number, default: 0 },
  warningOpen: { type: Boolean, default: false },
});

const emit = defineEmits([
  'next',
  'previous',
  'skip',
  'goto-next-page',
  'scroll-to-top',
]);

// Simplified state management
const isNavigating = ref(false);
const visitedSections = ref(new Set<string>());

const store = useStore();
const router = useRouter();

// Injected values with fallbacks
const nextSlide = inject('nextSlide', null);
const previousSlide = inject('previousSlide', null);
const goToNextPage = inject('goToNextPage', null);
const colorScheme = inject('colorScheme', 'green');
const currentSection = inject(
  'currentSection',
  computed(() => null),
);
const questionArea = inject('questionArea', ref(null));

// Core navigation flags
const isStartSection = computed(() => props.currentSectionIdx === 0);
const isEndSection = computed(
  () => props.currentSectionIdx === props.sectionsCount,
);
const isResultsSection = computed(
  () => props.currentSectionIdx === props.sectionsCount + 1,
);
const isContentSection = computed(
  () =>
    props.currentSectionIdx > 0 &&
    props.currentSectionIdx <= props.sectionsCount,
);

// Skip section logic
const canSkipSection = computed(() => {
  if (
    !isContentSection.value ||
    props.questionIndex !== 0 ||
    !currentSection.value
  )
    return false;

  const section = currentSection.value;
  const skippableTypes = ['kasam', 'physical'];
  return (
    skippableTypes.includes(section.pageName) &&
    !visitedSections.value.has(section.id)
  );
});

// Button text
const nextButtonText = computed(() => {
  if (isStartSection.value) return 'Starta!';
  if (isResultsSection.value) return 'Fortsätt';
  return 'Nästa';
});

// Navigation availability
const canNavigate = computed(
  () => !props.warningOpen && !props.isLoading && !isNavigating.value,
);

// Cleanup tracking
let navigationTimer: number | null = null;
let scrollTimer: number | null = null;
let saveTimer: number | null = null;

// Cleanup function
function cleanup() {
  if (navigationTimer) {
    clearTimeout(navigationTimer);
    navigationTimer = null;
  }
  if (scrollTimer) {
    clearTimeout(scrollTimer);
    scrollTimer = null;
  }
  if (saveTimer) {
    clearTimeout(saveTimer);
    saveTimer = null;
  }
  isNavigating.value = false;
}

// Session storage management
const SECTION_KEY = 'sectionIdx';
function restoreSectionIdx() {
  try {
    const savedIdx = sessionStorage.getItem(SECTION_KEY);
    if (savedIdx && parseInt(savedIdx) > 0) {
      emit('next', parseInt(savedIdx));
    }
  } catch (error) {
    console.error('Error restoring section:', error);
  }
}

// Watch for section changes with safety
watch(
  () => props.currentSectionIdx,
  newIdx => {
    if (newIdx > 0 && newIdx <= props.sectionsCount) {
      if (saveTimer) clearTimeout(saveTimer);
      saveTimer = setTimeout(() => {
        try {
          sessionStorage.setItem(SECTION_KEY, newIdx.toString());
          markSectionVisited();
        } catch (error) {
          console.error('Error saving section:', error);
        }
      }, 300);
    }
  },
);

// Visited sections management
function loadVisitedSections() {
  try {
    const visited = store.savePoints?.life?.visitedSections;
    if (visited?.length) {
      visitedSections.value = new Set(visited);
    }
  } catch (error) {
    console.error('Error loading visited sections:', error);
  }
}

async function markSectionVisited() {
  const sectionId = currentSection.value?.id;
  if (!sectionId || visitedSections.value.has(sectionId)) return;

  try {
    visitedSections.value.add(sectionId);

    const storeVisited = store.savePoints?.life?.visitedSections;
    if (Array.isArray(storeVisited) && !storeVisited.includes(sectionId)) {
      storeVisited.push(sectionId);
      await store.$patch();
    }
  } catch (error) {
    console.error('Error marking section visited:', error);
  }
}

// Safe scroll to top
function scrollToTop() {
  if (scrollTimer) clearTimeout(scrollTimer);

  scrollTimer = setTimeout(() => {
    try {
      emit('scroll-to-top');

      // Use RAF for DOM operations
      requestAnimationFrame(() => {
        // Simplified scroll logic
        if (questionArea?.value) {
          questionArea.value.scrollTo({ top: 0, behavior: 'smooth' });
        }

        const activeSlide = document.querySelector('.swiper-slide-active');
        if (activeSlide) {
          activeSlide.scrollTo({ top: 0, behavior: 'smooth' });
        }
      });
    } catch (error) {
      console.error('Error scrolling:', error);
    }
  }, 100);
}

// Navigation handlers with safety
async function handlePrevious() {
  if (!canNavigate.value) return;

  isNavigating.value = true;

  try {
    if (isContentSection.value && props.questionIndex > 0) {
      emit('previous', 'question');
    } else if (props.currentSectionIdx > 0) {
      emit('previous', 'section-last-question');
    } else {
      emit('previous', 'section');
    }

    scrollToTop();
  } catch (error) {
    console.error('Previous navigation error:', error);
  } finally {
    navigationTimer = setTimeout(() => {
      isNavigating.value = false;
    }, 500);
  }
}

async function handleNext(event?: Event) {
  event?.preventDefault();
  event?.stopPropagation();

  if (!canNavigate.value) return;

  // Handle results section navigation
  if (isResultsSection.value) {
    try {
      emit('goto-next-page');
      await router.push('/goals');
    } catch (error) {
      console.error('Navigation to goals error:', error);
      // Fallback navigation
      window.location.href = '/goals';
    }
    return;
  }

  isNavigating.value = true;

  try {
    if (isContentSection.value) {
      await markSectionVisited();
    }

    if (
      isContentSection.value &&
      props.questionIndex < props.questionCount - 1
    ) {
      emit('next', 'question');
    } else {
      emit('next', 'section');
    }

    scrollToTop();
  } catch (error) {
    console.error('Next navigation error:', error);
  } finally {
    navigationTimer = setTimeout(() => {
      isNavigating.value = false;
    }, 500);
  }
}

function skipSection() {
  if (!canSkipSection.value || !canNavigate.value) return;

  isNavigating.value = true;

  try {
    markSectionVisited();
    emit('skip');
    scrollToTop();
  } catch (error) {
    console.error('Skip section error:', error);
  } finally {
    navigationTimer = setTimeout(() => {
      isNavigating.value = false;
    }, 500);
  }
}

// Lifecycle with error handling
onMounted(async () => {
  try {
    loadVisitedSections();
    restoreSectionIdx();

    // Prevent iOS scroll issues
    await nextTick();
    Object.assign(document.body.style, {
      touchAction: 'manipulation',
      overscrollBehavior: 'none',
    });
  } catch (error) {
    console.error('Mount error:', error);
  }
});

// Cleanup
onBeforeUnmount(() => {
  cleanup();
});
</script>

<template>
  <div
    class="navigation-bar"
    :class="{
      'is-loading': isNavigating || isLoading,
      'results-section': isResultsSection,
    }"
  >
    <div class="navigation-content">
      <div class="button-grid">
        <b-button
          v-if="!isStartSection"
          @click.prevent="handlePrevious"
          look="secondary"
          class="back-button"
          :disabled="!canNavigate"
          :aria-disabled="!canNavigate"
        >
          Tillbaka
        </b-button>
        <div v-else class="back-button-placeholder"></div>

        <b-button
          @click.prevent="handleNext"
          class="next-button"
          :class="{ 'next-button-highlight': isResultsSection }"
          :disabled="!canNavigate"
          :aria-disabled="!canNavigate"
        >
          {{ nextButtonText }}
          <template v-if="isNavigating || isLoading">
            <span class="loading-indicator">
              <span class="loading-dot" v-for="i in 3" :key="i"></span>
            </span>
          </template>
        </b-button>
      </div>

      <div class="skip-button-container" v-if="canSkipSection">
        <b-button
          look="secondary"
          class="skip-button"
          @click.prevent="skipSection"
          :disabled="!canNavigate"
          :aria-disabled="!canNavigate"
        >
          Hoppa över testet
        </b-button>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Navigation Bar Styles */
.navigation-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  background-color: #f9fcf2;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  box-shadow: 0 -4px 10px rgba(0, 0, 0, 0.05);
  padding: 1rem;
  padding-bottom: calc(1rem + env(safe-area-inset-bottom, 0));
  z-index: 100;
  transform: translateZ(0);
  -webkit-transform: translateZ(0);
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;
  contain: layout style;
  will-change: opacity;
  transition: opacity 0.2s ease;
}

.navigation-bar.is-loading {
  opacity: 0.9;
}

.navigation-content {
  max-width: 800px;
  margin: 0 auto;
  padding-right: env(safe-area-inset-right, 0);
  padding-left: env(safe-area-inset-left, 0);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
}

.button-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  width: 100%;
}

.skip-button-container {
  width: 100%;
}

/* Button Styles */
.back-button,
.next-button,
.skip-button {
  width: 100%;
  min-height: 44px;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
  cursor: pointer;
  -webkit-appearance: none;
  appearance: none;
  -webkit-touch-callout: none;
  user-select: none;
  transform: translateZ(0);
  -webkit-transform: translateZ(0);
  isolation: isolate;
  will-change: transform;
  transition: transform 0.2s ease;
}

.back-button:disabled,
.next-button:disabled,
.skip-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  pointer-events: none;
}

.back-button-placeholder {
  width: 100%;
}

/* Active States */
.back-button:active:not(:disabled),
.next-button:active:not(:disabled),
.skip-button:active:not(:disabled) {
  transform: scale(0.98);
}

/* Loading Animation */
.loading-indicator {
  margin-left: 0.5rem;
  display: inline-flex;
  align-items: center;
}

.loading-dot {
  display: inline-block;
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background-color: currentColor;
  margin: 0 2px;
  animation: dotPulse 1.5s infinite;
}

.loading-dot:nth-child(2) {
  animation-delay: 0.2s;
}

.loading-dot:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes dotPulse {
  0%,
  60%,
  100% {
    transform: scale(1);
    opacity: 1;
  }
  30% {
    transform: scale(1.5);
    opacity: 0.7;
  }
}

/* iOS Specific Fixes */
@supports (-webkit-touch-callout: none) {
  .back-button,
  .next-button,
  .skip-button {
    padding: 14px 20px;
    font-size: 16px;
    line-height: 1.2;
  }

  .navigation-bar {
    padding-bottom: calc(1.25rem + env(safe-area-inset-bottom, 10px));
  }
}

/* Responsive Adjustments */
@media (min-width: 768px) {
  .navigation-content {
    padding: 0.25rem 0;
  }
}

@media (min-width: 1024px) {
  .navigation-content {
    align-items: center;
    justify-content: center;
  }

  .button-grid {
    max-width: 400px;
    width: 100%;
    margin: 0 auto;
  }

  .back-button,
  .next-button {
    padding: 0.875rem 1.5rem;
  }

  .skip-button {
    max-width: 400px;
    margin: 0 auto;
  }
}

/* iPhone Mini Optimization */
@media screen and (max-width: 375px) and (max-height: 667px) {
  .navigation-bar {
    padding: 0.75rem 0.5rem;
  }

  .back-button,
  .next-button,
  .skip-button {
    padding: 12px 16px;
  }

  .navigation-content {
    gap: 0.5rem;
  }

  .button-grid {
    gap: 0.75rem;
  }
}

/* High contrast mode */
@media (prefers-contrast: high) {
  .back-button,
  .next-button,
  .skip-button {
    border: 2px solid currentColor;
  }
}

/* Reduced motion */
@media (prefers-reduced-motion: reduce) {
  * {
    animation-duration: 0.01ms !important;
    transition-duration: 0.01ms !important;
  }

  .back-button:active:not(:disabled),
  .next-button:active:not(:disabled),
  .skip-button:active:not(:disabled) {
    transform: none;
  }
}
</style>
